import { type Row, type TableColumns, Tooltip } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpLink } from 'components/BpLink/BpLink';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useConfirm } from 'hooks/useConfirm';
import { useRooms } from 'matrix/hooks/useRooms';
import { useTranslation } from 'react-i18next';
import { getExternalService } from 'utils/getExternalServiceHelper';
import { naturalStringSort } from 'utils/stringHelper';
import type { GroupTableColumns, GroupTableType } from '../types/types';

export const UseGroupsTableFragment = graphql(`
  fragment UseGroupsTableFields on Query {
    groups(options: { sort: { name: ASC } }, where: $groupWhere) {
      __typename
      uuid
      organization {
        __typename
        uuid
        displayName
      }
      name
      managedBy
      admins {
        __typename
        uuid
        displayNameShort
        displayName
      }
      isCommunity
      schoolYear {
        __typename
        uuid
        name
      }
      editors {
        __typename
        uuid
      }
      viewers {
        __typename
        uuid
      }
      externalServices {
        __typename
        threema {
          __typename
          enabled
          lastSync
        }
        zoom {
          __typename
          enabled
          lastSync
        }
      }
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            directLink
            lastSync
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            lastSync
          }
        }
      }
    }
    onlineEvents: calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: $calendarEventWhere) {
      __typename
      uuid
      virtualLocations {
        __typename
        uuid
        running
      }
      holderConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            ... on Organization {
              __typename
              uuid
            }
            ... on Group {
              __typename
              uuid
            }
            ... on Profile {
              __typename
              uuid
            }
          }
        }
      }
    }
  }
`);

type useGroupsProps = {
  query: FragmentType<typeof UseGroupsTableFragment> | undefined;
  showGroupInfo?: boolean;
  columns?: GroupTableColumns;
  onNavigate?: (courseUuid: string) => void;
};

export const useGroupsTable = (props?: useGroupsProps) => {
  const { t } = useTranslation();
  const rooms = useRooms();
  const { query, showGroupInfo, columns, onNavigate } = props ?? {};

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('groups.titleSingular'),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const fragment = getFragmentData(UseGroupsTableFragment, query);
  const groups = fragment?.groups;
  const onlineEvents = fragment?.onlineEvents;

  /**
   * Maps incoming data from UseGroupsFragment to GroupTableType
   *
   * @returns {CourseTableType[]} An array of mapped data to use in course tables
   */
  function getTableData(): GroupTableType[] {
    return (
      groups?.map((group) => {
        const notificationRoom = rooms?.find((room) => room.name.includes(group.uuid));
        let newMessages = false;

        if (notificationRoom) {
          newMessages = notificationRoom.getRoomUnreadNotificationCount() > 0;
        }

        const nextCloud = group?.nextcloudConnection?.edges[0]?.properties;
        const threema = group?.threemaBroadcastConnection?.edges[0]?.properties;

        return {
          uuid: group.uuid,
          organizationUuid: group.organization.uuid,
          organizationName: group.organization.displayName ?? '',
          name: group.name,
          managedBy: group.managedBy,
          isCommunity: group.isCommunity,
          schoolyearName: group.schoolYear?.name ?? undefined,
          meetingInProgress:
            onlineEvents
              ?.find((v) => v.holderConnection.edges.find((e) => e.node.uuid)?.node.uuid === group.uuid)
              ?.virtualLocations.find((v) => v.running)?.running ?? false,
          admins: group.admins,
          adminShortNames: group.admins.map((admin) => admin.displayNameShort ?? '').sort(naturalStringSort),
          adminNames: group.admins.map((admin) => admin.displayName ?? '').sort(naturalStringSort),
          origin: '-',
          newMessage: newMessages,
          editors: group.editors,
          viewers: group.viewers,
          externalServices: {
            threema: {
              enabled: threema?.enabled ?? false,
              lastSync: threema?.lastSync ? dayjs(threema.lastSync) : undefined,
            },
            zoom: {
              enabled: group.externalServices?.zoom?.enabled ?? false,
              lastSync: group.externalServices?.zoom?.lastSync
                ? dayjs(group.externalServices?.zoom.lastSync)
                : undefined,
            },
            nextcloud: {
              enabled: nextCloud?.enabled ?? false,
              lastSync: nextCloud?.lastSync ? dayjs(nextCloud?.lastSync) : undefined,
            },
          },
        };
      }) ?? []
    );
  }

  /**
   * Generates an array of table column configurations based on the selected columns.
   *
   * @returns {TableColumns<GroupTableType>[]} An array of column configurations for groups tables
   * including:
   * - name (with BpLink component and navigation)
   * - organization (with tooltip)
   * - admins (with tooltip showing full names)
   * - schoolYear
   * - nextcloud status
   * - threema status
   * - origin
   * - community flag
   *
   * Each column is only included if specified in the `columns` parameter.
   */
  function getTableColumns(): TableColumns<GroupTableType>[] {
    const tableColumns: TableColumns<GroupTableType>[] = [];

    if (columns?.includes('name')) {
      tableColumns.push({
        size: 400,
        canExpand: true,
        header: t('common.designation'),
        id: 'name',
        accessorKey: 'name',
        cell: ({ row }: { row: Row<GroupTableType> }) => {
          return (
            <BpLink
              value={row.original.name}
              onNavigate={() => onNavigate && onNavigate(row.original.uuid)}
              isEditorOfCourse={false}
              inProgress={showGroupInfo && row.original.meetingInProgress}
              hasNews={showGroupInfo && row.original.newMessage}
            />
          );
        },
      });
    }

    if (columns?.includes('organization')) {
      tableColumns.push({
        header: t('institution.title'),
        id: 'organizationName',
        accessorKey: 'organizationName',
        size: 400,
        cell: ({ row }) => <Tooltip content={row.original.organizationName}>{row.original.organizationName}</Tooltip>,
      });
    }

    if (columns?.includes('admins')) {
      tableColumns.push({
        header: t('common.administration'),
        id: 'admins',
        accessorKey: 'admins',
        cell: ({ row }) => (
          <Tooltip content={row.original.adminNames.join('\n')}>{row.original.adminShortNames.join(', ')}</Tooltip>
        ),
      });
    }

    if (columns?.includes('schoolYear')) {
      tableColumns.push({
        header: t('schoolyears.titleSingular'),
        id: 'schoolyearName',
        accessorKey: 'schoolyearName',
        accessorFn: (row) => {
          return row.schoolyearName ?? t('groups.schoolyearSpanningShort');
        },
        size: 130,
      });
    }

    if (columns?.includes('origin')) {
      tableColumns.push({
        header: t('meetings.origin'),
        id: 'origin',
        accessorKey: 'origin',
      });
    }

    if (columns?.includes('nextcloud')) {
      tableColumns.push({
        header: t('common.nextcloud'),
        id: 'nextcloud',
        accessorKey: 'nextcloud',
        cell: ({ row }: { row: Row<GroupTableType> }) => {
          const service = getExternalService(
            row.original.externalServices?.nextcloud?.enabled ?? false,
            row.original.externalServices?.nextcloud?.lastSync,
          );
          return <div style={{ color: service.color ?? undefined }}>{service?.text?.toString() ?? ''}</div>;
        },
        size: 150,
      });
    }

    if (columns?.includes('threema')) {
      tableColumns.push({
        header: t('common.threema'),
        id: 'threema',
        accessorKey: 'threema',
        cell: ({ row }: { row: Row<GroupTableType> }) => {
          const service = getExternalService(
            row.original.externalServices?.threema?.enabled ?? false,
            row.original.externalServices?.threema?.lastSync,
          );
          return <div style={{ color: service.color ?? undefined }}>{service?.text?.toString() ?? ''}</div>;
        },
        size: 150,
      });
    }

    if (columns?.includes('community')) {
      tableColumns.push({
        header: t('common.community'),
        id: 'isCommunity',
        accessorKey: 'isCommunity',
        type: 'boolean',
        size: 75,
      });
    }

    return tableColumns;
  }

  return {
    data: getTableData(),
    columns: getTableColumns(),
    ConfirmationDialog,
    confirmDelete,
  };
};
